import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Calendar,
  CalendarDays,
  Clock,
  Users,
  ChevronLeft,
  ChevronRight,
  Plus,
  Check,
  X,
  Copy,
  ClipboardList,
  Image as ImageIcon,
  Upload
} from "lucide-react";
import { toast } from "sonner";

const Classes = () => {
  const { user, isMemberOrAbove, isCoachOrAdmin } = useAuth();
  const [classes, setClasses] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [viewMode, setViewMode] = useState("weekly"); // daily, weekly, monthly
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [attendance, setAttendance] = useState([]);
  
  const [newClass, setNewClass] = useState({
    name: "",
    description: "",
    date: "",
    startTime: "",
    endTime: "",
    capacity: 30,
    category: "fitness",
    imageUrl: ""
  });
  
  const [duplicateTarget, setDuplicateTarget] = useState("");

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toISOString().split('T')[0];
  }
  
  function getCurrentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  useEffect(() => {
    fetchData();
  }, [weekStart, selectedMonth, viewMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let classesRes;
      if (viewMode === "monthly") {
        classesRes = await axios.get(`${API}/classes?month=${selectedMonth}`);
      } else {
        classesRes = await axios.get(`${API}/classes?weekStart=${weekStart}`);
      }
      const bookingsRes = await axios.get(`${API}/bookings`);
      setClasses(classesRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      console.error("Classes fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };
  
  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const d = new Date(year, month - 1 + direction, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const bookClass = async (classId) => {
    if (!isMemberOrAbove()) {
      toast.error("Available for TRIBE members");
      return;
    }
    try {
      await axios.post(`${API}/bookings`, { classId });
      toast.success("Booked!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success("Booking cancelled");
      fetchData();
    } catch (err) {
      toast.error("Failed to cancel");
    }
  };

  const createClass = async () => {
    if (!newClass.name || !newClass.date || !newClass.startTime) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      await axios.post(`${API}/classes`, {
        ...newClass,
        coachId: user.id,
        coachName: user.name
      });
      toast.success("Class created");
      setShowCreateDialog(false);
      setNewClass({ name: "", description: "", date: "", startTime: "", endTime: "", capacity: 30, category: "fitness", imageUrl: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to create class");
    }
  };

  const duplicateClasses = async (type) => {
    if (!duplicateTarget) {
      toast.error("Please select target week");
      return;
    }
    try {
      const payload = { targetWeekStart: duplicateTarget };
      if (type === "single" && selectedClass) {
        payload.classId = selectedClass.id;
      } else {
        payload.weekStart = weekStart;
      }
      const res = await axios.post(`${API}/classes/duplicate`, payload);
      toast.success(`Duplicated ${res.data.duplicated} class(es)`);
      setShowDuplicateDialog(false);
      setDuplicateTarget("");
      fetchData();
    } catch (err) {
      toast.error("Failed to duplicate");
    }
  };

  const openAttendance = async (cls) => {
    setSelectedClass(cls);
    try {
      const res = await axios.get(`${API}/classes/${cls.id}/attendance`);
      setAttendance(res.data.attendees || []);
      setShowAttendanceDialog(true);
    } catch (err) {
      toast.error("Failed to load attendance");
    }
  };

  const markAttendance = async (userId, present) => {
    try {
      await axios.post(`${API}/classes/${selectedClass.id}/attendance`, { userId, present });
      setAttendance(prev => prev.map(a => a.userId === userId ? {...a, present} : a));
      toast.success(present ? "Marked present" : "Marked absent");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const isBooked = (classId) => bookings.some(b => b.classId === classId);
  const getBookingId = (classId) => bookings.find(b => b.classId === classId)?.id;

  // Date helpers
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const weekDates = days.map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  const formatWeekRange = () => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}`;
  };
  
  const formatMonth = () => {
    const [year, month] = selectedMonth.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });
  };

  // Get classes for current view
  const getViewClasses = () => {
    if (viewMode === "daily") {
      return classes.filter(c => c.date === selectedDate);
    }
    return classes;
  };

  // Class Card Component
  const ClassCard = ({ cls }) => {
    const booked = isBooked(cls.id);
    const spotsLeft = cls.capacity - cls.bookedCount;
    const isFull = spotsLeft <= 0;
    
    return (
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-xl overflow-hidden">
        {cls.imageUrl && (
          <div className="h-24 bg-cover bg-center" style={{ backgroundImage: `url(${cls.imageUrl})` }} />
        )}
        <CardContent className={`p-4 ${cls.imageUrl ? 'pt-3' : ''}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="font-medium text-[#051F17]">{cls.name}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-[#4A5D56]">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {cls.startTime} - {cls.endTime}
                </span>
                <span className={`flex items-center gap-1 ${isFull ? 'text-[#B85C4C] font-medium' : ''}`}>
                  <Users className="w-3 h-3" />
                  {isFull ? 'FULL' : `${spotsLeft} spots`}
                </span>
              </div>
              <p className="text-xs text-[#8FA39B] mt-1">{cls.coachName}</p>
            </div>
            
            <div className="flex flex-col gap-2">
              {booked ? (
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => cancelBooking(getBookingId(cls.id))}
                  className="border-[#0B3D2E] text-[#0B3D2E]"
                >
                  <Check className="w-4 h-4 mr-1" /> Booked
                </Button>
              ) : isFull ? (
                <Button variant="outline" size="sm" disabled className="opacity-50">
                  Full
                </Button>
              ) : (
                <Button 
                  size="sm"
                  onClick={() => bookClass(cls.id)}
                  className="bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white"
                  disabled={!isMemberOrAbove()}
                >
                  Book
                </Button>
              )}
              
              {isCoachOrAdmin() && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => openAttendance(cls)}
                  className="text-[#4A5D56]"
                >
                  <ClipboardList className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0B3D2E]/20 border-t-[#0B3D2E] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-5 pb-8 max-w-lg mx-auto" data-testid="classes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-brand text-[#0B3D2E]">Classes</h1>
          <p className="text-sm text-[#4A5D56]">Timetable</p>
        </div>
        {isCoachOrAdmin() && (
          <div className="flex gap-2">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => { setSelectedClass(null); setShowDuplicateDialog(true); }}
              className="border-[#0B3D2E]/30 text-[#0B3D2E]"
              title="Duplicate Week"
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#0B3D2E] hover:bg-[#0B3D2E]/90 text-white rounded-full"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        )}
      </div>

      {/* View Mode Selector */}
      <div className="flex gap-2 mb-4">
        {['daily', 'weekly', 'monthly'].map(mode => (
          <Button
            key={mode}
            variant={viewMode === mode ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode(mode)}
            className={viewMode === mode 
              ? "bg-[#0B3D2E] text-white" 
              : "border-[#0B3D2E]/20 text-[#4A5D56]"
            }
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </Button>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" size="icon" onClick={() => viewMode === "monthly" ? navigateMonth(-1) : navigateWeek(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          {viewMode === "monthly" ? (
            <CalendarDays className="w-4 h-4 text-[#0B3D2E]" />
          ) : (
            <Calendar className="w-4 h-4 text-[#0B3D2E]" />
          )}
          <span className="font-medium text-[#051F17]">
            {viewMode === "monthly" ? formatMonth() : formatWeekRange()}
          </span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => viewMode === "monthly" ? navigateMonth(1) : navigateWeek(1)}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Daily View - Date Selector */}
      {viewMode === "daily" && (
        <div className="flex gap-2 overflow-x-auto pb-4 mb-4 no-scrollbar">
          {weekDates.map((date, i) => {
            const d = new Date(date);
            const isSelected = date === selectedDate;
            const isToday = date === new Date().toISOString().split('T')[0];
            
            return (
              <button
                key={date}
                onClick={() => setSelectedDate(date)}
                className={`flex-shrink-0 w-14 py-2 px-1 rounded-xl text-center transition-all ${
                  isSelected 
                    ? 'bg-[#0B3D2E] text-white' 
                    : isToday 
                      ? 'bg-[#E3EBE5] text-[#0B3D2E]'
                      : 'bg-white text-[#4A5D56]'
                }`}
              >
                <p className="text-[10px] font-medium">{days[i]}</p>
                <p className="text-lg font-bold">{d.getDate()}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Classes List */}
      {viewMode === "daily" ? (
        <div className="space-y-3">
          {getViewClasses().length === 0 ? (
            <p className="text-center text-[#8FA39B] py-8">No classes on this day</p>
          ) : (
            getViewClasses().map(cls => <ClassCard key={cls.id} cls={cls} />)
          )}
        </div>
      ) : viewMode === "weekly" ? (
        <div className="space-y-4">
          {weekDates.map((date, i) => {
            const dayClasses = classes.filter(c => c.date === date);
            const d = new Date(date);
            const isToday = date === new Date().toISOString().split('T')[0];
            
            return (
              <div key={date}>
                <div className={`flex items-center gap-2 mb-2 ${isToday ? 'text-[#0B3D2E]' : 'text-[#4A5D56]'}`}>
                  <span className={`text-sm font-medium ${isToday ? 'font-bold' : ''}`}>
                    {days[i]} {d.getDate()}
                  </span>
                  {isToday && <span className="text-xs bg-[#0B3D2E] text-white px-2 py-0.5 rounded-full">Today</span>}
                </div>
                
                {dayClasses.length === 0 ? (
                  <p className="text-sm text-[#8FA39B] pl-2 pb-2">No classes</p>
                ) : (
                  <div className="space-y-2">
                    {dayClasses.map(cls => <ClassCard key={cls.id} cls={cls} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Monthly view - simplified calendar grid
        <div className="space-y-2">
          {Array.from(new Set(classes.map(c => c.date))).sort().map(date => {
            const dayClasses = classes.filter(c => c.date === date);
            const d = new Date(date);
            const isToday = date === new Date().toISOString().split('T')[0];
            
            return (
              <div key={date} className="mb-4">
                <div className={`flex items-center gap-2 mb-2 ${isToday ? 'text-[#0B3D2E]' : 'text-[#4A5D56]'}`}>
                  <span className={`text-sm font-medium ${isToday ? 'font-bold' : ''}`}>
                    {d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </span>
                  {isToday && <span className="text-xs bg-[#0B3D2E] text-white px-2 py-0.5 rounded-full">Today</span>}
                </div>
                <div className="space-y-2">
                  {dayClasses.map(cls => <ClassCard key={cls.id} cls={cls} />)}
                </div>
              </div>
            );
          })}
          {classes.length === 0 && (
            <p className="text-center text-[#8FA39B] py-8">No classes this month</p>
          )}
        </div>
      )}

      {/* Booking info for free users */}
      {!isMemberOrAbove() && (
        <div className="mt-6 p-4 bg-[#E3EBE5]/30 rounded-xl text-center">
          <p className="text-sm text-[#4A5D56]">
            Class booking is available for TRIBE members.
          </p>
        </div>
      )}

      {/* Create Class Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-brand">Create Class</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <div className="space-y-4 pr-4">
              <div>
                <Label>Class Name</Label>
                <Input
                  placeholder="e.g., HIIT, Yoga, Strength"
                  value={newClass.name}
                  onChange={(e) => setNewClass({...newClass, name: e.target.value})}
                  className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description..."
                  value={newClass.description}
                  onChange={(e) => setNewClass({...newClass, description: e.target.value})}
                  className="min-h-[60px] bg-[#F9F9F7] border-0 rounded-xl resize-none"
                />
              </div>
              <div>
                <Label>Class Image URL (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="https://..."
                    value={newClass.imageUrl}
                    onChange={(e) => setNewClass({...newClass, imageUrl: e.target.value})}
                    className="h-12 bg-[#F9F9F7] border-0 rounded-xl flex-1"
                  />
                  <div className="w-12 h-12 bg-[#F9F9F7] rounded-xl flex items-center justify-center">
                    {newClass.imageUrl ? (
                      <img src={newClass.imageUrl} alt="" className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-[#8FA39B]" />
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newClass.date}
                    onChange={(e) => setNewClass({...newClass, date: e.target.value})}
                    className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={newClass.capacity}
                    onChange={(e) => setNewClass({...newClass, capacity: parseInt(e.target.value) || 30})}
                    className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={newClass.startTime}
                    onChange={(e) => setNewClass({...newClass, startTime: e.target.value})}
                    className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
                  />
                </div>
                <div>
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={newClass.endTime}
                    onChange={(e) => setNewClass({...newClass, endTime: e.target.value})}
                    className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
                  />
                </div>
              </div>
              <Button onClick={createClass} className="w-full h-12 bg-[#0B3D2E] text-white rounded-full">
                Create Class
              </Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Attendance Dialog */}
      <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="font-brand">Class Attendance</DialogTitle>
          </DialogHeader>
          {selectedClass && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F9F9F7] rounded-xl">
                <p className="font-medium text-[#051F17]">{selectedClass.name}</p>
                <p className="text-sm text-[#4A5D56]">{selectedClass.date} • {selectedClass.startTime}</p>
              </div>
              
              <ScrollArea className="max-h-[300px]">
                {attendance.length === 0 ? (
                  <p className="text-center text-[#8FA39B] py-4">No bookings yet</p>
                ) : (
                  <div className="space-y-2">
                    {attendance.map(a => (
                      <div key={a.userId} className="flex items-center justify-between p-3 bg-white border border-[#0B3D2E]/5 rounded-xl">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#0B3D2E]/10 flex items-center justify-center overflow-hidden">
                            {a.profilePhoto ? (
                              <img src={a.profilePhoto} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-sm font-medium text-[#0B3D2E]">
                                {a.userName?.charAt(0) || '?'}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#051F17] text-sm">{a.userName}</p>
                            <p className="text-xs text-[#8FA39B]">{a.userEmail}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant={a.present ? "default" : "outline"}
                            size="sm"
                            onClick={() => markAttendance(a.userId, true)}
                            className={a.present ? "bg-[#0B3D2E] text-white" : "border-[#0B3D2E]/30"}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={!a.present ? "default" : "outline"}
                            size="sm"
                            onClick={() => markAttendance(a.userId, false)}
                            className={!a.present ? "bg-[#B85C4C] text-white" : "border-[#B85C4C]/30"}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-brand">
              {selectedClass ? 'Duplicate Class' : 'Duplicate Week'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#4A5D56]">
              {selectedClass 
                ? `Duplicate "${selectedClass.name}" to another week.`
                : `Duplicate all classes from ${formatWeekRange()} to another week.`
              }
            </p>
            <div>
              <Label>Target Week Start (Monday)</Label>
              <Input
                type="date"
                value={duplicateTarget}
                onChange={(e) => setDuplicateTarget(e.target.value)}
                className="h-12 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
            <Button 
              onClick={() => duplicateClasses(selectedClass ? "single" : "week")}
              className="w-full h-12 bg-[#0B3D2E] text-white rounded-full"
            >
              <Copy className="w-4 h-4 mr-2" /> Duplicate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Classes;
